import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { CostDeepDiveUnit } from "../../../utils/models";

type Props = {
	unit: CostDeepDiveUnit;
};

export const CostDeepDiveHeader: FC<Props> = ({ unit }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { chosenDepartment } = useChosenDepartment();
	const { track } = useAnalytics();

	const handleBack = useCallback(() => {
		const qs = searchParams.toString();
		navigate(`/${chosenDepartment.department_id}/management-center${qs ? `?${qs}` : ""}`);
	}, [searchParams, navigate, chosenDepartment.department_id]);

	const handleHoursSummary = useCallback(() => {
		track("hours_summary_opened", { department_id: chosenDepartment.department_id });
		const qs = searchParams.toString();
		navigate(`/${chosenDepartment.department_id}/hours-summary${qs ? `?${qs}` : ""}`);
	}, [searchParams, navigate, chosenDepartment.department_id, track]);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				width: "100%",
				mb: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<IconButton onClick={handleBack} aria-label={t("cost.deep.dive.back")}>
					<ArrowBackIcon />
				</IconButton>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 600,
					}}
				>
					{t(unit === CostDeepDiveUnit.Hours ? "cost.deep.dive.hours.title" : "cost.deep.dive.title")}
				</Typography>
			</Box>
			<SystemButton buttonVariant="secondary" size="small" leadingIcon={<AccessTimeIcon />} onClick={handleHoursSummary} showBadge="beta">
				{t("hours.summary.title")}
			</SystemButton>
		</Box>
	);
};
